import { useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { useOrganization } from "@/hooks/use-organization";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FormItem, FormLabel } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

interface JobOfferTemplateSelectProps {
  onApply: (templateId: string) => void;
}

export function JobOfferTemplateSelect({
  onApply,
}: JobOfferTemplateSelectProps) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const buId = selectedOrgBusinessUnit.id;

  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const { data: templates, isLoading } = useQuery(
    trpc.jobOffer.templateList.queryOptions({
      organizationBusinessUnitId: buId,
    }),
  );

  const deleteMutation = useMutation(
    trpc.jobOffer.templateDelete.mutationOptions(),
  );

  const selectedTemplate = templates?.find((t) => t.id === selectedId);

  const handleChange = (value: string) => {
    if (value === "__placeholder__") {
      return;
    }
    setSelectedId(value);
    onApply(value);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTemplate) {
      return;
    }
    const { id, name } = selectedTemplate;

    try {
      await deleteMutation.mutateAsync({
        organizationBusinessUnitId: buId,
        id,
      });

      await queryClient.invalidateQueries(
        trpc.jobOffer.templateList.queryOptions({
          organizationBusinessUnitId: buId,
        }),
      );

      setSelectedId(undefined);
      setIsDeleteOpen(false);
      toast.success(`Template "${name}" deleted`);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to delete template",
      );
    }
  };

  if (isLoading || !templates?.length) {
    return null;
  }

  return (
    <>
      <FormItem>
        <FormLabel className="font-medium">Apply Template</FormLabel>
        <div className="flex items-stretch gap-2">
          <Select
            value={selectedId ?? "__placeholder__"}
            onValueChange={handleChange}
            disabled={isLoading}
          >
            <SelectTrigger className="focus-visible:ring-blue-500">
              <SelectValue placeholder="Select a template to apply..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__placeholder__" disabled>
                Select a template to apply...
              </SelectItem>
              {templates?.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            disabled={!selectedTemplate}
            onClick={() => setIsDeleteOpen(true)}
            aria-label="Delete selected template"
            className="shrink-0 h-auto px-4 text-gray-500 hover:text-red-600 hover:border-red-300"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </FormItem>

      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>
              {selectedTemplate ? (
                <>
                  Are you sure you want to delete the template{" "}
                  <span className="font-medium text-gray-900">
                    "{selectedTemplate.name}"
                  </span>
                  ? This cannot be undone.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDeleteOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
